import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, number, object, string } from 'prop-types';
import classNames from 'classnames';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { ASPECT_HEIGHT_LISTING, ASPECT_WIDTH_LISTING } from '../../config/configLayout';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { getPriceFromListing } from '../../util/price';
import { showToast } from '../../util/toast';
import { LISTING_STATE_CLOSED, LISTING_STATE_DRAFT, propTypes } from '../../util/types';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';

import AspectRatioWrapper from '../AspectRatioWrapper/AspectRatioWrapper';
import NamedLink from '../NamedLink/NamedLink';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import { ChipButton, PrimaryButton, SecondaryButton } from '../mui/Button';
import { SWDialog } from '../mui/Dialog';
import { InlineTextButton } from '../Button/Button';
import Menu from '../Menu/Menu';
import MenuLabel from '../MenuLabel/MenuLabel';
import MenuContent from '../MenuContent/MenuContent';
import MenuItem from '../MenuItem/MenuItem';
import layoverCss from '../../styles/layoverDialog.module.css';
import { SHARE_PAGE_MODE_LISTING_PUBLISHED } from '../../containers/ShareListingPage/def';
import CopyListingMenuItem from '../CopyListingMenuItem/CopyListingMenuItem';
import { getListingCopyMenuState } from '../../util/listingCopy';
import {
  filterMyListingsByStatus,
  getMyListingsStatusCounts,
  isTrashedListing,
  MY_LISTINGS_FILTER,
} from './MyListingsSection.helpers';
import css from './MyListingsSection.module.css';

const MY_LISTINGS_STATUS_STORAGE_KEY = 'soulwise.myListingsStatusFilter';
const MENU_CONTENT_OFFSET = -4;

const resolveStoredStatusFilter = () => {
  if (typeof window === 'undefined') {
    return MY_LISTINGS_FILTER.ALL;
  }
  try {
    const stored = window.sessionStorage.getItem(MY_LISTINGS_STATUS_STORAGE_KEY);
    const valid = Object.values(MY_LISTINGS_FILTER);
    return valid.includes(stored) ? stored : MY_LISTINGS_FILTER.ALL;
  } catch (e) {
    return MY_LISTINGS_FILTER.ALL;
  }
};

const persistStatusFilter = status => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (status === MY_LISTINGS_FILTER.ALL) {
      window.sessionStorage.removeItem(MY_LISTINGS_STATUS_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(MY_LISTINGS_STATUS_STORAGE_KEY, status);
    }
  } catch (e) {
    // Ignore storage failures (private mode, etc.)
  }
};

const LoadingOrErrorMaybe = props => {
  const { queryInProgress, queryListingsError } = props;

  if (queryInProgress) {
    return (
      <div className={css.feedback}>
        <FormattedMessage id="MyListingsSection.loadingListings" />
      </div>
    );
  }

  if (queryListingsError) {
    return (
      <div className={css.feedback}>
        <FormattedMessage id="MyListingsSection.queryError" />
      </div>
    );
  }

  return null;
};

const EmptyState = () => (
  <div className={css.emptyState}>
    <div className={css.emptyIcon} aria-hidden="true">
      <svg width="34" height="34" viewBox="0 0 34 34" focusable="false">
        <path d="M17 5v24M5 17h24" />
        <path d="M26 4l1.4 3.2L31 8.5l-3.3 1.4L26.2 13l-1.4-3.1L21 8.5l3.6-1.3L26 4z" />
      </svg>
    </div>
    <h3 className={css.emptyTitle}>
      <FormattedMessage id="MyListingsSection.emptyTitle" />
    </h3>
    <p className={css.emptyCopy}>
      <FormattedMessage id="MyListingsSection.emptyCopy" />
    </p>
    <NamedLink className={css.createListingLink} name="NewListingPage">
      <FormattedMessage id="MyListingsSection.createListing" />
    </NamedLink>
  </div>
);

const FilterButton = props => {
  const { isActive, messageId, count, onClick } = props;
  return (
    <ChipButton
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-pressed={isActive}
      variant="contained"
      color={isActive ? 'primary' : 'default'}
      className={classNames(isActive && 'MuiButton-isSelected')}
      onClick={onClick}
    >
      <FormattedMessage id={messageId} values={{ count }} />
    </ChipButton>
  );
};

FilterButton.propTypes = {
  isActive: bool.isRequired,
  messageId: string.isRequired,
  count: number.isRequired,
  onClick: func.isRequired,
};

const ListingCard = props => {
  const {
    listing,
    renderSizes,
    onTrashListing,
    onRestoreListing,
    onCloseListing,
    onCopyListing,
    actionsInProgress,
    hasCopyingError,
  } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const intl = useIntl();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const currentListing = ensureOwnListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', state } = currentListing.attributes;
  const slug = createSlug(title);
  const isDraft = state === LISTING_STATE_DRAFT;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isTrashed = isTrashedListing(currentListing);
  const showCloseListing = !isClosed && !isTrashed && !isDraft;
  const busy = !!actionsInProgress;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const {
    aspectWidth = ASPECT_WIDTH_LISTING,
    aspectHeight = ASPECT_HEIGHT_LISTING,
    variantPrefix = 'listing-card',
  } = config.layout?.listingImage || {};
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants || {}).filter(k => k.startsWith(variantPrefix))
    : [];
  let formattedPrice = null;
  try {
    formattedPrice = getPriceFromListing(currentListing, config, intl)?.formattedPrice || null;
  } catch (e) {
    formattedPrice = null;
  }
  const viewLinkName = isDraft ? 'EditListingPage' : 'ListingPage';
  const viewLinkParams = isDraft
    ? { id, slug, type: LISTING_PAGE_PARAM_TYPE_DRAFT, tab: 'details' }
    : { id, slug };
  const editListingLinkType = isDraft
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const editParams = { id, slug, type: editListingLinkType, tab: 'details' };
  const menuItemClasses = classNames(css.menuItem, {
    [css.menuItemDisabled]: busy,
  });

  const closeMenu = () => setIsMenuOpen(false);

  const handleRestore = event => {
    event.preventDefault();
    event.stopPropagation();
    if (busy || !onRestoreListing) {
      return;
    }
    closeMenu();
    onRestoreListing(currentListing.id);
  };

  const handleShare = event => {
    event.preventDefault();
    event.stopPropagation();
    closeMenu();
    history.push(
      createResourceLocatorString(
        'ShareListingPage',
        routeConfiguration,
        {
          id,
          slug,
          type: SHARE_PAGE_MODE_LISTING_PUBLISHED,
        },
        {}
      )
    );
  };

  const handleCopyListing = event => {
    event.preventDefault();
    event.stopPropagation();
    if (busy || !onCopyListing) {
      return;
    }
    closeMenu();
    Promise.resolve(onCopyListing(currentListing.id)).catch(() => {
      // PractitionerDashboardPage surfaces the toast; the card shows an inline error.
    });
  };

  const handleCloseListing = event => {
    event.preventDefault();
    event.stopPropagation();
    if (busy || !onCloseListing) {
      return;
    }
    closeMenu();
    onCloseListing(currentListing.id);
  };

  const handleConfirmDelete = () => {
    setIsDeleteConfirmOpen(false);
    if (!onTrashListing) {
      return;
    }
    Promise.resolve(onTrashListing(currentListing.id)).catch(e => {
      const blockers = e?.blockers || e?.data?.blockers || [];
      let messageId = 'ManageListingCard.trashBlocked';
      if (blockers.includes('active_subscriptions') && blockers.includes('future_bookings')) {
        messageId = 'ManageListingCard.trashBlockedBoth';
      } else if (blockers.includes('active_subscriptions')) {
        messageId = 'ManageListingCard.trashBlockedSubscriptions';
      } else if (blockers.includes('future_bookings')) {
        messageId = 'ManageListingCard.trashBlockedFutureBookings';
      } else if (blockers.includes('future_bookings_check_failed')) {
        messageId = 'ManageListingCard.trashBlockedCheckFailed';
      } else if (e?.code === 'listing_trash_blocked' || e?.message === 'listing_trash_blocked') {
        messageId = 'ManageListingCard.trashBlocked';
      } else {
        return;
      }
      showToast(intl.formatMessage({ id: messageId }), { autoHideDuration: 8000 });
    });
  };

  return (
    <div className={css.card}>
      <div className={css.cardMedia}>
        <NamedLink className={css.cardMainLink} name={viewLinkName} params={viewLinkParams}>
          <AspectRatioWrapper
            className={css.imageWrapper}
            width={aspectWidth}
            height={aspectHeight}
          >
            <ResponsiveImage
              rootClassName={css.image}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
          </AspectRatioWrapper>
        </NamedLink>
        <div className={css.menubar}>
          <Menu
            className={css.menu}
            contentPlacementOffset={MENU_CONTENT_OFFSET}
            contentPosition="left"
            fullWidthOnMobile={false}
            pinToViewport
            useArrow={false}
            isOpen={isMenuOpen}
            onToggleActive={setIsMenuOpen}
          >
            <MenuLabel
              className={css.menuLabel}
              isOpenClassName={css.menuLabelOpen}
              aria-label={intl.formatMessage({ id: 'ManageListingCard.listingActionsMenu' })}
            >
              <span className={css.menuIconWrapper} aria-hidden="true">
                <MoreHorizIcon fontSize="small" />
              </span>
            </MenuLabel>
            <MenuContent rootClassName={css.menuContent}>
              {[
                isTrashed ? (
                  <MenuItem key="restore-listing">
                    <InlineTextButton
                      rootClassName={menuItemClasses}
                      disabled={busy}
                      onClick={handleRestore}
                    >
                      <FormattedMessage id="ManageListingCard.restoreListing" />
                    </InlineTextButton>
                  </MenuItem>
                ) : null,
                !isTrashed ? (
                  <MenuItem key="share-listing">
                    <InlineTextButton
                      rootClassName={menuItemClasses}
                      disabled={busy}
                      onClick={handleShare}
                    >
                      <FormattedMessage id="ManageListingCard.shareListing" />
                    </InlineTextButton>
                  </MenuItem>
                ) : null,
                getListingCopyMenuState(currentListing).show ? (
                  <MenuItem key="copy-listing">
                    <CopyListingMenuItem
                      listing={currentListing}
                      busy={busy}
                      menuItemClassName={menuItemClasses}
                      testId="my-listings-card-copy"
                      onCopy={handleCopyListing}
                    />
                  </MenuItem>
                ) : null,
                showCloseListing ? (
                  <MenuItem key="close-listing">
                    <InlineTextButton
                      rootClassName={menuItemClasses}
                      disabled={busy}
                      onClick={handleCloseListing}
                    >
                      <FormattedMessage id="ManageListingCard.closeListing" />
                    </InlineTextButton>
                  </MenuItem>
                ) : null,
                !isTrashed ? (
                  <MenuItem key="delete-listing">
                    <InlineTextButton
                      rootClassName={menuItemClasses}
                      disabled={busy}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        closeMenu();
                        setIsDeleteConfirmOpen(true);
                      }}
                    >
                      <FormattedMessage id="ManageListingCard.deleteListing" />
                    </InlineTextButton>
                  </MenuItem>
                ) : null,
              ].filter(Boolean)}
            </MenuContent>
          </Menu>
        </div>
      </div>
      <NamedLink className={css.cardMainLink} name={viewLinkName} params={viewLinkParams}>
        <div className={css.cardBody}>
          <div className={css.cardTitle}>{title}</div>
          {formattedPrice ? <div className={css.cardPrice}>{formattedPrice}</div> : null}
          {isDraft ? (
            <span className={css.draftBadge}>
              <FormattedMessage id="MyListingsSection.draftBadge" />
            </span>
          ) : null}
          {hasCopyingError ? (
            <div className={css.copyError} role="alert">
              <FormattedMessage
                id="ManageListingCard.copyListingFailed"
                defaultMessage="Could not copy this listing. Please try again."
              />
            </div>
          ) : null}
        </div>
      </NamedLink>
      <div className={css.cardFooter}>
        <NamedLink className={css.editLink} name="EditListingPage" params={editParams}>
          <FormattedMessage id="ManageListingCard.editListing" />
        </NamedLink>
      </div>

      <SWDialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        aria-labelledby={`my-listings-delete-title-${id}`}
        PaperProps={{ sx: { maxWidth: 520, width: '100%' } }}
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle id={`my-listings-delete-title-${id}`}>
          <FormattedMessage id="ManageListingCard.deleteConfirmTitle" />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="p">
            <FormattedMessage id="ManageListingCard.deleteConfirmMessage" />
          </Typography>
          <div className={layoverCss.actions}>
            <SecondaryButton
              type="button"
              onClick={() => setIsDeleteConfirmOpen(false)}
              className={layoverCss.secondaryAction}
            >
              <FormattedMessage id="ManageListingCard.deleteConfirmCancel" />
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={handleConfirmDelete}
              className={layoverCss.primaryAction}
            >
              <FormattedMessage id="ManageListingCard.deleteConfirmSubmit" />
            </PrimaryButton>
          </div>
        </DialogContent>
      </SWDialog>
    </div>
  );
};

ListingCard.defaultProps = {
  renderSizes: null,
  onTrashListing: null,
  onRestoreListing: null,
  onCloseListing: null,
  onCopyListing: null,
  actionsInProgress: false,
  hasCopyingError: false,
};

ListingCard.propTypes = {
  listing: propTypes.ownListing.isRequired,
  renderSizes: string,
  onTrashListing: func,
  onRestoreListing: func,
  onCloseListing: func,
  onCopyListing: func,
  actionsInProgress: bool,
  hasCopyingError: bool,
};

export const MyListingsSection = props => {
  const {
    className,
    rootClassName,
    listings,
    loadData,
    queryInProgress,
    queryListingsError,
    renderSizes,
    onTrashListing,
    onRestoreListing,
    onCloseListing,
    onCopyListing,
    actionsInProgress,
    copyingErrorListingId,
  } = props;
  const intl = useIntl();
  const [activeFilter, setActiveFilter] = useState(resolveStoredStatusFilter);
  const setFilter = status => {
    setActiveFilter(status);
    persistStatusFilter(status);
  };
  const classes = classNames(rootClassName || css.root, className);
  const statusCounts = getMyListingsStatusCounts(listings);
  const totalListings = listings.length;
  const hasListings = totalListings > 0;
  const filteredListings = filterMyListingsByStatus(listings, activeFilter);

  useEffect(() => {
    if (loadData) {
      loadData();
    }
  }, [loadData]);

  return (
    <section className={classes} aria-label={intl.formatMessage({ id: 'MyListingsSection.title' })}>
      {!hasListings ? (
        <>
          <div className={css.emptySectionLabel}>
            <FormattedMessage id="MyListingsSection.emptySectionLabel" />
          </div>
          <LoadingOrErrorMaybe
            queryInProgress={queryInProgress}
            queryListingsError={queryListingsError}
          />
          {!queryInProgress && !queryListingsError ? <EmptyState /> : null}
        </>
      ) : (
        <>
          <div className={css.header}>
            <h2 className={css.title}>
              <FormattedMessage id="MyListingsSection.title" />
            </h2>
            <div className={css.headerActions}>
              <NamedLink className={css.addListingLink} name="NewListingPage">
                <span className={css.addListingIcon} aria-hidden="true">
                  +
                </span>
                <FormattedMessage id="MyListingsSection.createListing" />
              </NamedLink>
              <span className={css.headerActionsSeparator} aria-hidden="true">
                |
              </span>
              <NamedLink className={css.viewAllLink} name="ManageListingsPage">
                <FormattedMessage id="MyListingsSection.viewAll" />
              </NamedLink>
            </div>
          </div>
          <LoadingOrErrorMaybe
            queryInProgress={queryInProgress}
            queryListingsError={queryListingsError}
          />
          <div className={css.filters} role="tablist">
            <FilterButton
              isActive={activeFilter === MY_LISTINGS_FILTER.ALL}
              messageId="MyListingsSection.allFilter"
              count={statusCounts[MY_LISTINGS_FILTER.ALL]}
              onClick={() => setFilter(MY_LISTINGS_FILTER.ALL)}
            />
            <FilterButton
              isActive={activeFilter === MY_LISTINGS_FILTER.ACTIVE}
              messageId="MyListingsSection.activeFilter"
              count={statusCounts[MY_LISTINGS_FILTER.ACTIVE]}
              onClick={() => setFilter(MY_LISTINGS_FILTER.ACTIVE)}
            />
            <FilterButton
              isActive={activeFilter === MY_LISTINGS_FILTER.DRAFT}
              messageId="MyListingsSection.draftFilter"
              count={statusCounts[MY_LISTINGS_FILTER.DRAFT]}
              onClick={() => setFilter(MY_LISTINGS_FILTER.DRAFT)}
            />
            <FilterButton
              isActive={activeFilter === MY_LISTINGS_FILTER.TRASH}
              messageId="MyListingsSection.trashFilter"
              count={statusCounts[MY_LISTINGS_FILTER.TRASH]}
              onClick={() => setFilter(MY_LISTINGS_FILTER.TRASH)}
            />
          </div>
          {filteredListings.length > 0 ? (
            <div className={css.cardScroller}>
              {filteredListings.map(item => (
                <ListingCard
                  key={item.id.uuid}
                  listing={item}
                  renderSizes={renderSizes}
                  onTrashListing={onTrashListing}
                  onRestoreListing={onRestoreListing}
                  onCloseListing={onCloseListing}
                  onCopyListing={onCopyListing}
                  actionsInProgress={actionsInProgress}
                  hasCopyingError={
                    !!copyingErrorListingId &&
                    (copyingErrorListingId.uuid || copyingErrorListingId) === item.id.uuid
                  }
                />
              ))}
            </div>
          ) : (
            <div className={css.feedback}>
              <FormattedMessage
                id={
                  activeFilter === MY_LISTINGS_FILTER.TRASH
                    ? 'ManageListingsPage.trashEmpty'
                    : 'MyListingsSection.noFilteredListings'
                }
              />
            </div>
          )}
        </>
      )}
    </section>
  );
};

MyListingsSection.defaultProps = {
  className: null,
  rootClassName: null,
  listings: [],
  loadData: null,
  queryInProgress: false,
  queryListingsError: null,
  renderSizes: '(max-width: 767px) 74vw, 248px',
  onTrashListing: null,
  onRestoreListing: null,
  onCloseListing: null,
  onCopyListing: null,
  actionsInProgress: false,
  copyingErrorListingId: null,
};

MyListingsSection.propTypes = {
  className: string,
  rootClassName: string,
  listings: arrayOf(propTypes.ownListing),
  loadData: func,
  queryInProgress: bool,
  queryListingsError: propTypes.error,
  renderSizes: string,
  onTrashListing: func,
  onRestoreListing: func,
  onCloseListing: func,
  onCopyListing: func,
  actionsInProgress: bool,
  copyingErrorListingId: object,
};

export default MyListingsSection;
